import React, { useMemo, useRef, useState } from "react";
import { MdCheck, MdContentCopy, MdContentPaste, MdFactCheck, MdRestore, MdSave, MdSmartToy } from "react-icons/md";
import { IssueList, OkLine, TokenInspector, copyText } from "./EditorParts";
import ContentTree from "./ContentTree";
import { buildAiTemplatePrompt, compareStructures, diffTokens, parseAiResponse, parseJsonWithLocation, scalarTokenPaths, tokensIn, validateEmailHtml, isPlainObject } from "./contentUtils";

// HTML console of one email template: email-safe HTML stays HTML (never
// converted to React), {{tokens}} are inspected live, a save is reviewed
// first (detected / new / removed tokens, missing protected tokens block it)
// and an AI answer is analyzed before it can replace anything.
export default function HtmlConsole({ html, savedHtml, originalHtml, onChange, onSave, saving, registry, t, document, onApplyDocument, tokenSyntax, emailType, settings, notify, readOnly }) {
  const area = useRef(null);
  const [review, setReview] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiReport, setAiReport] = useState(null);
  const unknownTokens = settings?.validation?.unknownTokens || "warn";
  const checkStructure = settings?.validation?.html !== false;
  const diff = useMemo(() => diffTokens(savedHtml, html, registry), [savedHtml, html, registry]);
  const dirty = html !== savedHtml;

  const insertToken = (name) => {
    if (readOnly) return;
    const el = area.current;
    const tokenText = `{{${name}}}`;
    const start = el ? el.selectionStart : html.length;
    const end = el ? el.selectionEnd : html.length;
    onChange(`${html.slice(0, start)}${tokenText}${html.slice(end)}`);
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(start + tokenText.length, start + tokenText.length); } });
  };

  const startSave = () => setReview(validateEmailHtml(html, { registry, unknownTokens, checkStructure }));
  const confirmSave = async () => { const ok = await onSave(html); if (ok !== false) setReview(null); };

  const aiPrompt = () => buildAiTemplatePrompt({
    html: settings?.ai?.includeHtml === false ? "(not shared)" : html, document: settings?.ai?.includeJson === false ? {} : document, protectedTokens: registry?.protected || [], tokens: tokensIn(html), tokenSyntax, emailType, customInstructions: settings?.ai?.customInstructions,
  });

  const analyzeAi = () => {
    const parsed = parseAiResponse(aiText);
    const report = { html: parsed.html, json: null, jsonError: null, htmlCheck: null, tokens: null, keys: null };
    if (parsed.json !== null) {
      const result = parseJsonWithLocation(parsed.json);
      if (!result.ok) report.jsonError = result.error.message;
      else if (!isPlainObject(result.value)) report.jsonError = t.rootMustBeObject;
      else { report.json = result.value; report.keys = compareStructures(document || {}, result.value); }
    }
    if (parsed.html) {
      // Keys the AI added to the JSON make their new tokens known.
      const extended = report.json ? { ...registry, contentPaths: [...new Set([...(registry?.contentPaths || []), ...scalarTokenPaths(report.json)])] } : registry;
      report.htmlCheck = validateEmailHtml(parsed.html, { registry: extended, unknownTokens, checkStructure: true });
      report.tokens = diffTokens(html, parsed.html, extended);
    }
    if (!parsed.html && parsed.json === null) report.empty = true;
    setAiReport(report);
  };
  const applyAi = () => {
    if (aiReport.html) onChange(aiReport.html);
    if (aiReport.json && onApplyDocument) onApplyDocument(aiReport.json);
    setAiOpen(false); setAiText(""); setAiReport(null);
    notify?.(t.aiApplied);
  };

  return <div className="eck-html">
    <div className="eck-toolbar">
      <button type="button" className="eck-btn" onClick={() => copyText(html).then(() => notify?.(t.copied))}><MdContentCopy aria-hidden="true" /> {t.copyHtml}</button>
      <button type="button" className="eck-btn" onClick={() => copyText(aiPrompt()).then(() => notify?.(t.aiPromptCopied))}><MdSmartToy aria-hidden="true" /> {t.copyAiPrompt}</button>
      {!readOnly && <button type="button" className="eck-btn" onClick={() => setAiOpen((open) => !open)} aria-expanded={aiOpen}><MdContentPaste aria-hidden="true" /> {t.importAi}</button>}
      <button type="button" className="eck-btn" onClick={startSave}><MdFactCheck aria-hidden="true" /> {t.validate}</button>
      {!readOnly && <button type="button" className="eck-btn" disabled={!dirty} onClick={() => { if (window.confirm(t.confirmResetHtml)) onChange(savedHtml); }}><MdRestore aria-hidden="true" /> {t.resetSaved}</button>}
      {!readOnly && originalHtml && <button type="button" className="eck-btn" disabled={html === originalHtml} onClick={() => { if (window.confirm(t.confirmRestoreOriginal)) onChange(originalHtml); }}><MdRestore aria-hidden="true" /> {t.restoreOriginal}</button>}
      <span className="eck-toolbar__spacer" />
      {!readOnly && <button type="button" className="eck-btn eck-btn--primary" disabled={!dirty || saving} onClick={startSave}><MdSave aria-hidden="true" /> {saving ? t.saving : t.saveHtml}</button>}
    </div>

    {aiOpen && <div className="eck-panel">
      <p className="eck-hint">{t.importAiHint}</p>
      <textarea className="eck-code__area eck-code__area--short" aria-label={t.importAi} spellCheck={false} value={aiText} onChange={(e) => { setAiText(e.target.value); setAiReport(null); }} placeholder={"```html\n...\n```\n```json\n{ ... }\n```"} />
      <div className="eck-toolbar">
        <button type="button" className="eck-btn" disabled={!aiText.trim()} onClick={analyzeAi}><MdFactCheck aria-hidden="true" /> {t.analyze}</button>
        <span className="eck-toolbar__spacer" />
        {aiReport && !aiReport.empty && !aiReport.jsonError && <button type="button" className="eck-btn eck-btn--primary" onClick={applyAi}><MdCheck aria-hidden="true" /> {t.applyToEditor}</button>}
      </div>
      {aiReport?.empty && <div className="eck-alert">{t.aiNothingFound}</div>}
      {aiReport?.tokens && <div className="eck-report">
        <h5>{t.htmlChanges}</h5>
        <TokenInspector diff={aiReport.tokens} t={t} />
        <IssueList errors={aiReport.htmlCheck.errors} warnings={aiReport.htmlCheck.warnings} t={t} />
      </div>}
      {aiReport?.jsonError && <div className="eck-alert">{aiReport.jsonError}</div>}
      {aiReport?.keys && <div className="eck-report">
        <h5>{t.jsonChanges}</h5>
        {aiReport.keys.extra.length > 0 && <p><strong>{t.newKeys}</strong> {aiReport.keys.extra.join(", ")}</p>}
        {aiReport.keys.missing.length > 0 && <p className="eck-field__error"><strong>{t.removedKeys}</strong> {aiReport.keys.missing.join(", ")}</p>}
        {!aiReport.keys.extra.length && !aiReport.keys.missing.length && <OkLine>{t.sameKeys}</OkLine>}
      </div>}
      {aiReport && <p className="eck-hint">{t.applyNotSaved}</p>}
    </div>}

    {review && <div className={`eck-panel eck-review${review.valid ? "" : " is-blocked"}`} role="region" aria-label={t.saveReview}>
      <h5>{t.saveReview}</h5>
      <TokenInspector diff={diff} t={t} />
      <IssueList errors={review.errors} warnings={review.warnings} t={t} />
      {review.valid && !review.warnings.length && <OkLine>{t.htmlValid}</OkLine>}
      {!review.valid && <p className="eck-field__error">{t.saveBlocked}</p>}
      <div className="eck-toolbar">
        <button type="button" className="eck-btn" onClick={() => setReview(null)}>{t.close}</button>
        <span className="eck-toolbar__spacer" />
        {!readOnly && dirty && <button type="button" className="eck-btn eck-btn--primary" disabled={!review.valid || saving} onClick={confirmSave}><MdSave aria-hidden="true" /> {saving ? t.saving : t.confirmSave}</button>}
      </div>
    </div>}

    <div className="eck-html__body">
      <textarea ref={area} className="eck-code__area eck-code__area--html" aria-label={t.htmlTab} spellCheck={false} value={html} readOnly={readOnly} onChange={(e) => onChange(e.target.value)} />
      <aside className="eck-html__side">
        <TokenInspector diff={diff} t={t} onInsert={insertToken} />
        {!readOnly && <details className="eck-insert" open>
          <summary>{t.dynamicContent}</summary>
          <ContentTree document={document} t={t} usedTokens={tokensIn(html)} onInsert={(token) => insertToken(tokenSyntax === "content" ? `content.${token}` : token)} />
        </details>}
      </aside>
    </div>
  </div>;
}
